import { LegalClientFinancialMonitoringParam } from 'api/types/params';
import { isYes } from 'helpers';
import { YesOrNoType } from 'types/yesOrNo';

import { CompanyGlobalFinancialMonitoringFormType } from '../../forms/companyGlobalFinancialMonitoringForm';

export const getGlobalFinancialMonitoringParam = (
  values: CompanyGlobalFinancialMonitoringFormType
): Omit<LegalClientFinancialMonitoringParam, 'financeInfo'> => {
  const isSubjectToRegulation =
    values.globalFinancialMonitoring.isSubjectToRegulation?.value ===
    YesOrNoType.Yes;

  if (!isSubjectToRegulation) {
    return { isSubjectToRegulation };
  }

  return {
    isSubjectToRegulation,
    globalInfo: {
      supervisoryAuthorityNameAndLocation:
        values.globalFinancialMonitoring.supervisoryAuthorityNameAndLocation!,

      hasMandatoryAmlRegulations: isYes(
        values.globalFinancialMonitoring.mandatoryAmlRegulation
          ?.hasMandatoryAmlRegulations
      ),

      mandatoryAmlRegulation:
        values.globalFinancialMonitoring.mandatoryAmlRegulation
          ?.hasMandatoryAmlRegulations?.value === YesOrNoType.Yes
          ? {
              regulationDetails:
                values.globalFinancialMonitoring.mandatoryAmlRegulation
                  .regulationDetails!,
              authorizedBodyName:
                values.globalFinancialMonitoring.mandatoryAmlRegulation
                  .authorizedBodyName!,
            }
          : undefined,

      lastAmlAuditResult: values.globalFinancialMonitoring.lastAmlAuditResult,

      hadInvestigationsOrSanctions:
        values.globalFinancialMonitoring.investigationsOrSanctions
          ?.hadInvestigationsOrSanctions?.value === YesOrNoType.Yes,
      amlDecisionDateAndAuthorityInfo:
        values.globalFinancialMonitoring.investigationsOrSanctions
          ?.hadInvestigationsOrSanctions?.value === YesOrNoType.Yes
          ? values.globalFinancialMonitoring.investigationsOrSanctions
              .amlDecisionDateAndAuthorityInfo
          : undefined,

      hasInternalAmlDocuments:
        values.globalFinancialMonitoring.internalAmlDocuments
          ?.hasInternalAmlDocuments?.value === YesOrNoType.Yes,

      internalAmlDocuments:
        values.globalFinancialMonitoring.internalAmlDocuments
          ?.hasInternalAmlDocuments?.value === YesOrNoType.Yes
          ? {
              adoptionDate:
                values.globalFinancialMonitoring.internalAmlDocuments
                  .adoptionDate!,
              lastUpdateDate:
                values.globalFinancialMonitoring.internalAmlDocuments
                  .lastUpdateDate!,
              hasEffectivenessAssessmentProcedures:
                values.globalFinancialMonitoring.internalAmlDocuments
                  .hasEffectivenessAssessmentProcedures?.value ===
                YesOrNoType.Yes,
              lastEffectivenessCheckInfo:
                values.globalFinancialMonitoring.internalAmlDocuments
                  .hasEffectivenessAssessmentProcedures?.value ===
                YesOrNoType.Yes
                  ? values.globalFinancialMonitoring.internalAmlDocuments
                      .lastEffectivenessCheckInfo
                  : undefined,
              appliesToForeignAffiliates:
                values.globalFinancialMonitoring.internalAmlDocuments
                  .appliesToForeignAffiliates?.value === YesOrNoType.Yes,
            }
          : undefined,

      hasDepartmentsResponsibleForAmlFunctions:
        values.globalFinancialMonitoring.departmentsResponsibleForAmlFunctions
          ?.hasDepartmentsResponsibleForAmlFunctions?.value === YesOrNoType.Yes,
      amlFunctionDepartmentsNames:
        values.globalFinancialMonitoring.departmentsResponsibleForAmlFunctions
          ?.hasDepartmentsResponsibleForAmlFunctions?.value === YesOrNoType.Yes
          ? values.globalFinancialMonitoring
              .departmentsResponsibleForAmlFunctions.amlFunctionDepartmentsNames
          : undefined,

      hasResponsibleAmlManager:
        values.globalFinancialMonitoring.responsibleAmlManager
          ?.hasResponsibleAmlManager?.value === YesOrNoType.Yes,

      responsibleAmlManager:
        values.globalFinancialMonitoring.responsibleAmlManager
          ?.hasResponsibleAmlManager?.value === YesOrNoType.Yes
          ? {
              fullName:
                values.globalFinancialMonitoring.responsibleAmlManager
                  .fullName!,
              position:
                values.globalFinancialMonitoring.responsibleAmlManager
                  .position!,
              email:
                values.globalFinancialMonitoring.responsibleAmlManager.email!,
              phoneNumber:
                values.globalFinancialMonitoring.responsibleAmlManager
                  .phoneNumber!,
            }
          : undefined,

      hasRiskManagementProcedures:
        values.globalFinancialMonitoring.hasRiskManagementProcedures?.value ===
        YesOrNoType.Yes,

      hasSourceOfFundsProcedures: isYes(
        values.globalFinancialMonitoring.hasSourceOfFundsProcedures
      ),
      hasRelationshipsWithPEPProcedures: isYes(
        values.globalFinancialMonitoring.hasRelationshipsWithPEPProcedures
      ),
      hasAnonymousAccounts: isYes(
        values.globalFinancialMonitoring.hasAnonymousAccounts
      ),
      hasAnonymousAccountPreventionProcedures: isYes(
        values.globalFinancialMonitoring.hasAnonymousAccountPreventionProcedures
      ),
      hasTransactionMonitoringProcedures: isYes(
        values.globalFinancialMonitoring.hasTransactionMonitoringProcedures
      ),
      hasAssetFreezingProcedures: isYes(
        values.globalFinancialMonitoring.hasAssetFreezingProcedures
      ),

      hasAMLTrainingProcedures: isYes(
        values.globalFinancialMonitoring.hasAMLTrainingProcedures
      ),
      amlTrainingDetails: isYes(
        values.globalFinancialMonitoring.hasAMLTrainingProcedures
      )
        ? values.globalFinancialMonitoring.amlTrainingDetails
        : undefined,

      hasBranchesInNonFATFCountries: isYes(
        values.globalFinancialMonitoring.hasBranchesInNonFATFCountries
      ),
      nonFATFBranchesNamesAndLocations: isYes(
        values.globalFinancialMonitoring.hasBranchesInNonFATFCountries
      )
        ? values.globalFinancialMonitoring.nonFATFBranchesNamesAndLocations
        : undefined,

      hasCorrespondentsInNonFATFCountries: isYes(
        values.globalFinancialMonitoring.hasCorrespondentsInNonFATFCountries
      ),
      nonFATFCorrespondentsNames: isYes(
        values.globalFinancialMonitoring.hasCorrespondentsInNonFATFCountries
      )
        ? values.globalFinancialMonitoring.nonFATFCorrespondentsNames
        : undefined,

      hasCorrespondentsInOffshoreZones: isYes(
        values.globalFinancialMonitoring.hasCorrespondentsInOffshoreZones
      ),
      offshoreCorrespondentsNames: isYes(
        values.globalFinancialMonitoring.hasCorrespondentsInOffshoreZones
      )
        ? values.globalFinancialMonitoring.offshoreCorrespondentsNames
        : undefined,

      hasShellBankRelationships: isYes(
        values.globalFinancialMonitoring.hasShellBankRelationships
      ),
      shellBanksNames: isYes(
        values.globalFinancialMonitoring.hasShellBankRelationships
      )
        ? values.globalFinancialMonitoring.shellBanksNames
        : undefined,

      hasShellBankPreventionProcedures: isYes(
        values.globalFinancialMonitoring.hasShellBankPreventionProcedures
      ),
      hasCDDRequestComplianceProcedures: isYes(
        values.globalFinancialMonitoring.hasCDDRequestComplianceProcedures
      ),

      creditRating: values.globalFinancialMonitoring.creditRating,
      subsidiariesNamesAndLocations:
        values.globalFinancialMonitoring.subsidiariesNamesAndLocations,
      keyPartnersNames: values.globalFinancialMonitoring.keyPartnersNames,
      foreignBranchesNamesAndLocations:
        values.globalFinancialMonitoring.foreignBranchesNamesAndLocations,
      financialGroupInfo: values.globalFinancialMonitoring.financialGroupInfo,

      hasSanctionPolicyDocuments: isYes(
        values.globalFinancialMonitoring.hasSanctionPolicyDocuments
      ),
      hasSanctionsMonitoringProcedures: isYes(
        values.globalFinancialMonitoring.hasSanctionsMonitoringProcedures
      ),
      isUnderSanctionsOrControlledBySanctionedEntity: isYes(
        values.globalFinancialMonitoring
          .isUnderSanctionsOrControlledBySanctionedEntity
      ),
      isListedInSanctionLists: isYes(
        values.globalFinancialMonitoring.isListedInSanctionLists
      ),
      isLinkedToSanctionedEntities: isYes(
        values.globalFinancialMonitoring.isLinkedToSanctionedEntities
      ),
    },
  };
};
